import React, { useState, useEffect, useMemo } from "react";
import { FaPlus, FaTrash, FaSave } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

const LogicRouting = ({ modeler, onClose }) => {
  const [routes, setRoutes] = useState([]);
  const [availableTargets, setAvailableTargets] = useState([]);
  const [maxRoutes, setMaxRoutes] = useState(0);
  const [selectedElement, setSelectedElement] = useState(null);
  const [keywordConditionUsed, setKeywordConditionUsed] = useState(""); // Tracks specific condition: "use node keywords", "has keywords", or ""
  const [errorDialog, setErrorDialog] = useState({ isOpen: false, message: "" });

  // "suggests" will be added on ai workflow embedding
  const conditions = ["use node keywords", "has keywords", "is equal to", "not equal to", "less than", "greater than"];
  const keywordConditions = useMemo(() => ["use node keywords", "has keywords"], []);

  useEffect(() => {
    if (!modeler) return;

    const selectionChanged = (event) => {
      const element = event.newSelection?.[0];
      if (!element) return;

      setSelectedElement(element);

      const businessObject = element.businessObject;
      if (!businessObject) {
        setMaxRoutes(0);
        setAvailableTargets([]);
        setKeywordConditionUsed("");
        return;
      }

      // Get all outgoing sequence flows and their targets
      const outgoing = businessObject.outgoing || [];

      // Extract target tasks (with both name and id)
      const targets = outgoing
        .map(flow => flow.targetRef)
        .filter(target => target && target.$type === 'bpmn:Task' && target.name)
        .map(task => ({
          name: task.name,
          id: task.id
        }))
        .filter((task, index, self) =>
          self.findIndex(t => t.name === task.name) === index
        );

      setMaxRoutes(outgoing.length);
      setAvailableTargets(targets);

      // Load existing routes if any
      const existingRoutes = businessObject.get('node:route_logic');
      if (existingRoutes) {
        try {
          const parsedRoutes = JSON.parse(existingRoutes);
          const loadedRoutes = parsedRoutes.map(route => ({
            condition: route[0],
            value: route[1],
            // Find the target name using the stored ID
            targetLabel: targets.find(t => t.id === route[2])?.name || ""
          }));
          setRoutes(loadedRoutes);
          // Set keywordConditionUsed to the first keyword condition found, or "" if none
          const firstKeywordCondition = loadedRoutes.find(route => keywordConditions.includes(route.condition))?.condition || "";
          setKeywordConditionUsed(firstKeywordCondition);
        } catch (e) {
          console.error("Failed to parse existing routes", e);
        }
      } else {
        setRoutes([]);
        setKeywordConditionUsed("");
      }
    };

    modeler.on('selection.changed', selectionChanged);

    // Check initial selection
    const selection = modeler.get('selection');
    if (selection && selection.get().length > 0) {
      selectionChanged({ newSelection: selection.get() });
    }

    return () => {
      modeler.off('selection.changed', selectionChanged);
    };
  }, [modeler, keywordConditions]);

  const handleAddRoute = () => {
    setRoutes([
      ...routes,
      {
        condition: keywordConditionUsed || conditions[0],
        value: "",
        targetLabel: availableTargets[0]?.name || "",
      },
    ]);
  };

  const handleRouteChange = (index, field, value) => {
    const updatedRoutes = [...routes];
    updatedRoutes[index] = { ...updatedRoutes[index], [field]: value };
    setRoutes(updatedRoutes);

    // Update keywordConditionUsed based on all routes
    if (field === "condition") {
      const firstKeywordCondition = updatedRoutes.find(route => keywordConditions.includes(route.condition))?.condition || "";
      setKeywordConditionUsed(firstKeywordCondition);
    }
  };

  const handleRemoveRoute = (index) => {
    const updatedRoutes = routes.filter((_, i) => i !== index);
    setRoutes(updatedRoutes);
    // Update keywordConditionUsed based on remaining routes
    const firstKeywordCondition = updatedRoutes.find(route => keywordConditions.includes(route.condition))?.condition || "";
    setKeywordConditionUsed(firstKeywordCondition);
  };

  const handleSaveRoutes = () => {
    if (!selectedElement) return;

    if (routes.length > 1) {
      // 1. Identify all keyword routes
      const keywordRoutes = routes.filter(route => keywordConditions.includes(route.condition));

      if (keywordRoutes.length > 0) {
        // If one or more keyword conditions are used, ALL routes must use the exact SAME keyword condition.
        const firstKeywordCondition = keywordRoutes[0].condition;
        
        // Check if every single route (including non-keyword ones) matches the chosen keyword condition.
        const allRoutesUniformKeyword = routes.every(route => route.condition === firstKeywordCondition);

        if (!allRoutesUniformKeyword) {
          setErrorDialog({
            isOpen: true,
            message: `If '${keywordConditions.join("' or '")}' is used, all logical routes must use the same, single condition from that group (e.g., all must be 'use node keywords'). Mixed conditions are not allowed.`,
          });
          return;
        }
      } 
      // 2. If NO keyword condition is used, non-keyword conditions can mix.
    }

    // --- SAVE LOGIC ---

    const modeling = modeler.get('modeling');
    const elementRegistry = modeler.get('elementRegistry');
    const element = elementRegistry.get(selectedElement.id);

    // Convert routes to the required format
    const routesToSave = routes.map(route => {
      const targetTask = availableTargets.find(t => t.name === route.targetLabel);
      return [
        route.condition,
        route.value,
        targetTask?.id || ""
      ];
    });

    // Update the custom property
    modeling.updateProperties(element, {
      'node:route_logic': JSON.stringify(routesToSave)
    });

    onClose();
  };

  const closeErrorDialog = () => {
    setErrorDialog({ isOpen: false, message: "" });
  };

  const maxRoutesReached = routes.length >= maxRoutes;
  const hasOutgoingPaths = maxRoutes > 0;

  return (
    <div
      className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 rounded-lg shadow-md w-full max-w-2xl"
      style={{ zIndex: 10000 }}
    >
      {errorDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10005]">
          <div className="bg-white p-4 rounded-lg shadow-md max-w-md">
            <p className="text-red-500 text-center">{errorDialog.message}</p>
            <div className="flex justify-center mt-4">
              <button
                onClick={closeErrorDialog}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleAddRoute}
          className={`flex items-center space-x-2 text-white px-4 py-2 rounded ${
            maxRoutesReached || !hasOutgoingPaths
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600'
          }`}
          style={{ zIndex: 10001 }}
          disabled={maxRoutesReached || !hasOutgoingPaths}
        >
          <FaPlus />
          <span>Add Logical Route</span>
        </button>
        <div className="flex space-x-2">
          <button
            onClick={handleSaveRoutes}
            className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            <FaSave />
            <span>Save Routes</span>
          </button>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            style={{ zIndex: 10001 }}
          >
            <IoClose size={20} />
          </button>
        </div>
      </div>

      {hasOutgoingPaths ? (
        <>
          <div className="text-sm text-gray-500 mb-2">
            {routes.length} of {maxRoutes} routes used
          </div>
          {routes.length === 0 && (
            <p className="text-gray-500 text-center">
              No logical routes defined. Click 'Add Logical Route' to start.
            </p>
          )}
        </>
      ) : (
        <p className="text-gray-500 text-center">
          {selectedElement
            ? `Selected element (${selectedElement.type}) has no outgoing paths`
            : "No element selected"}
        </p>
      )}

      <div className="flex flex-col space-y-4">
        {routes.map((route, index) => (
          <div key={index} className="flex items-center space-x-4">
            <span className="font-medium">Response</span>
            <select
              value={route.condition}
              onChange={(e) => handleRouteChange(index, "condition", e.target.value)}
              className="border p-2 rounded w-1/4"
            >
              {conditions.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={route.value}
              onChange={(e) => handleRouteChange(index, "value", e.target.value)}
              placeholder="Enter value"
              className="border p-2 rounded w-1/4"
            />
            <span className="font-medium">then</span>
            <select
              value={route.targetLabel}
              onChange={(e) => handleRouteChange(index, "targetLabel", e.target.value)}
              className="border p-2 rounded w-1/4"
              disabled={availableTargets.length === 0}
            >
              {availableTargets.length > 0 ? (
                availableTargets.map((target) => (
                  <option key={target.id} value={target.name}>
                    {target.name}
                  </option>
                ))
              ) : (
                <option value="">No valid targets available</option>
              )}
            </select>
            <button
              onClick={() => handleRemoveRoute(index)}
              className="text-red-500 hover:text-red-700"
              aria-label="Remove route"
            >
              <FaTrash />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LogicRouting;